import { useCallback, useEffect, useMemo, useState } from "react"
import { loadStripe } from "@stripe/stripe-js"
import {
  CardElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js"
import { Button } from "~/react/ui/button"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
} from "~/react/ui/dialog"
import { Icon } from "~/react/ui/icon"
import { useDialog } from "~/react/hooks/useDialog"
import { terms_of_service_path } from "~/lib/routes"
import { cn, getCsrfToken } from "~/lib/utils"
import {
  CardFormDisplay,
  CardLogo,
} from "~/react/components/email_accounts_center/add_email_account_slots_modal/card_form_display"
import {
  formatCardExpiration,
  formatDate,
} from "~/react/components/email_accounts_center/add_email_account_slots_modal/utils"
import type {
  BillingInfo,
  CreditCardInfo,
  PricePerWarmup,
} from "~/react/components/email_accounts_center/add_email_account_slots_modal/constants"
import { Confetti } from "../confetti"

type InboxProtectionModalProps = {
  isOpen: boolean
  onClose: () => void
  onLearnMore: (event: React.MouseEvent<HTMLButtonElement>) => void
  emailAccountIDs: number[]
  stripePublicKey?: string | null
  creditCardInfo?: CreditCardInfo | null
  cardholderName?: string | null
  billingInfo?: BillingInfo | null
  userEmail?: string | null
  pricePerWarmup: PricePerWarmup
}

type PlanType = "monthly" | "yearly"

const planOptions: Array<{
  value: PlanType
  titleKey: string
  descriptionKey: string
  priceKey: string
  totalKey: string
  oldPriceKey?: string
  recommendedKey?: string
}> = [
  {
    value: "monthly",
    titleKey:
      "js.email_accounts_center.email_account.warmup.protection_modal.plans.monthly.title",
    descriptionKey:
      "js.email_accounts_center.email_account.warmup.protection_modal.plans.monthly.description",
    priceKey:
      "js.email_accounts_center.email_account.warmup.protection_modal.plans.monthly.price",
    totalKey:
      "js.email_accounts_center.email_account.warmup.protection_modal.plans.monthly.total",
  },
  {
    value: "yearly",
    titleKey:
      "js.email_accounts_center.email_account.warmup.protection_modal.plans.yearly.title",
    descriptionKey:
      "js.email_accounts_center.email_account.warmup.protection_modal.plans.yearly.description",
    priceKey:
      "js.email_accounts_center.email_account.warmup.protection_modal.plans.yearly.price",
    totalKey:
      "js.email_accounts_center.email_account.warmup.protection_modal.plans.yearly.total",
    oldPriceKey:
      "js.email_accounts_center.email_account.warmup.protection_modal.plans.yearly.old_price",
    recommendedKey:
      "js.email_accounts_center.email_account.warmup.protection_modal.plans.yearly.recommended",
  },
]

export function InboxProtectionModal({
  isOpen,
  onClose,
  onLearnMore,
  emailAccountIDs,
  stripePublicKey,
  creditCardInfo,
  cardholderName: initialCardholderName,
  billingInfo,
  userEmail,
  pricePerWarmup,
}: InboxProtectionModalProps) {
  const { dialogRef, handleBackdropClick, stopPropagation, close } = useDialog({
    isOpen,
    onClose,
  })
  const [selectedPlan, setSelectedPlan] = useState<PlanType>("yearly")
  const [isSuccess, setIsSuccess] = useState(false)
  const [isEditingCard, setIsEditingCard] = useState(false)
  const [isSavingCard, setIsSavingCard] = useState(false)
  const [cardholderName, setCardholderName] = useState(
    initialCardholderName || "",
  )
  const [currentCreditCard, setCurrentCreditCard] =
    useState<CreditCardInfo | null | undefined>(creditCardInfo)
  const stripePromise = useMemo(
    () => (stripePublicKey ? loadStripe(stripePublicKey) : null),
    [stripePublicKey],
  )

  useEffect(() => {
    if (isOpen) {
      setIsSuccess(false)
      setIsEditingCard(false)
      setIsSavingCard(false)
      setCurrentCreditCard(creditCardInfo)
      setCardholderName(initialCardholderName || "")
    }
  }, [isOpen, creditCardInfo, initialCardholderName])

  const handleEditPayment = useCallback(
    (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
      event.preventDefault()
      setIsEditingCard(true)
    },
    [],
  )

  const handleCancelEditCard = useCallback(() => {
    setIsEditingCard(false)
    setCardholderName(initialCardholderName || "")
  }, [initialCardholderName])

  const selectedCount = emailAccountIDs.length
  const isMulti = selectedCount > 1
  const hasSelection = selectedCount > 0
  const modalDescriptionKey = isMulti
    ? "js.email_accounts_center.email_account.warmup.protection_modal.description_multi"
    : "js.email_accounts_center.email_account.warmup.protection_modal.description"
  const modalTitleKey = isMulti
    ? "js.email_accounts_center.email_account.warmup.protection_modal.title_multi"
    : "js.email_accounts_center.email_account.warmup.protection_modal.title"
  const primarySuccessMessageKey = isMulti
    ? "js.email_accounts_center.email_account.warmup.protection_modal.success.message_primary_multi"
    : "js.email_accounts_center.email_account.warmup.protection_modal.success.message_primary"
  const isCardFormVisible = isEditingCard || !currentCreditCard
  const perAccountTotal = pricePerWarmup[selectedPlan]
  const totalDueToday = perAccountTotal * selectedCount
  const formattedCardExpiration = formatCardExpiration(
    currentCreditCard?.expiration_month,
    currentCreditCard?.expiration_year,
  )
  const nextInvoiceDate = formatDate(
    (() => {
      const nextDate = new Date()
      if (selectedPlan === "yearly") {
        nextDate.setFullYear(nextDate.getFullYear() + 1)
      } else {
        nextDate.setMonth(nextDate.getMonth() + 1)
      }
      return nextDate.toISOString()
    })(),
  )

  type StripeInstance = ReturnType<typeof useStripe>
  type StripeElementsInstance = ReturnType<typeof useElements>

  const formatUsdShort = (amount: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)

  const formatUsdTotal = (amount: number) => `${formatUsdShort(amount)} USD`

  const handleSaveCard = useCallback(
    async (stripe: StripeInstance, elements: StripeElementsInstance) => {
      if (isSavingCard) return
      setIsSavingCard(true)

      try {
        if (!stripe || !elements) {
          window.App?.displayFlash?.(
            i18n.t("js.email_accounts_center.add_slots.stripe_loading"),
            "error",
          )
          return
        }

        const cardElement = elements.getElement(CardElement)
        if (!cardElement) {
          window.App?.displayFlash?.(
            i18n.t("js.email_accounts_center.add_slots.card_error"),
            "error",
          )
          return
        }

        const billingDetails: {
          name: string
          email?: string
          address?: {
            line1: string
            city: string
            postal_code: string
            country: string
          }
        } = {
          name: cardholderName,
          email: userEmail || undefined,
        }

        if (billingInfo) {
          billingDetails.address = {
            line1: billingInfo.address || "",
            city: billingInfo.city || "",
            postal_code: billingInfo.postal_code || "",
            country: billingInfo.country_code || "",
          }
        }

        const result = await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
          billing_details: billingDetails,
        })

        if (result.error) {
          window.App?.displayFlash?.(
            result.error.message ||
              i18n.t("js.email_accounts_center.add_slots.card_error"),
            "error",
          )
          return
        }

        const response = await fetch("/credit-cards/1", {
          method: "PATCH",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "X-CSRF-Token": getCsrfToken(),
          },
          body: JSON.stringify({
            payment_method_id: result.paymentMethod.id,
            full_name: cardholderName,
          }),
          credentials: "same-origin",
        })

        const data = await response.json()
        if (!response.ok) {
          window.App?.displayFlash?.(
            data.error ||
              i18n.t("js.email_accounts_center.add_slots.card_error"),
            "error",
          )
          return
        }

        window.App?.displayFlash?.(
          i18n.t("js.email_accounts_center.add_slots.card_saved"),
          "success",
        )

        const pm = result.paymentMethod
        setCurrentCreditCard({
          card_type: pm.card?.brand || "Card",
          last_4: pm.card?.last4 || "****",
          expiration_month: pm.card?.exp_month || null,
          expiration_year: pm.card?.exp_year?.toString() || null,
        })
        setIsEditingCard(false)
      } catch (error) {
        window.App?.displayFlash?.(
          i18n.t("js.email_accounts_center.add_slots.card_error"),
          "error",
        )
      } finally {
        setIsSavingCard(false)
      }
    },
    [
      isSavingCard,
      cardholderName,
      billingInfo,
      userEmail,
      setCurrentCreditCard,
      setIsEditingCard,
    ],
  )

  const renderMainContent = ({
    stripe,
    elements,
    showCardElement,
  }: {
    stripe: StripeInstance
    elements: StripeElementsInstance
    showCardElement: boolean
  }) => (
    <>
      <div className="email-account-warmup-modal__main">
        <div className="email-account-warmup-modal__header">
          <div>
            <h2 className="email-account-warmup-modal__title">
              {i18n.t(modalTitleKey, { count: selectedCount })}
            </h2>
            <p className="email-account-warmup-modal__description">
              {i18n.t(modalDescriptionKey)}{" "}
              {!isMulti ? (
                <>
                  <Button
                    variant="link"
                    size="sm"
                    type="button"
                    className="email-account-warmup-modal__link p-0"
                    onClick={onLearnMore}
                  >
                    {i18n.t(
                      "js.email_accounts_center.email_account.warmup.protection_modal.learn_more",
                    )}
                  </Button>
                  .
                </>
              ) : null}
            </p>
          </div>
          <Button
            variant="ghost"
            iconOnly
            className="email-account-warmup-modal__close"
            aria-label={i18n.t("js.cancel")}
            type="button"
            onClick={close}
          >
            <Icon icon="times" variant="regular" aria-hidden="true" />
          </Button>
        </div>

        {isMulti ? (
          <div className="email-account-warmup-modal__notice">
            <Icon
              icon="info-circle"
              size="sm"
              className="email-account-warmup-modal__notice-icon"
            />
            <span>
              {i18n.t(
                "js.email_accounts_center.email_account.warmup.protection_modal.multi_notice",
                { count: selectedCount },
              )}
            </span>
          </div>
        ) : null}

        <div className="email-account-warmup-modal__section">
          <h3 className="email-account-warmup-modal__section-title">
            {i18n.t(
              "js.email_accounts_center.email_account.warmup.protection_modal.select_subscription",
            )}
          </h3>
          {isMulti ? (
            <p className="email-account-warmup-modal__hint">
              {i18n.t(
                "js.email_accounts_center.email_account.warmup.protection_modal.per_account_pricing",
              )}
            </p>
          ) : null}

          <div className="email-account-warmup-modal__options">
            {planOptions.map((option) => {
              const isSelected = selectedPlan === option.value
              return (
                <label
                  key={option.value}
                  className={cn(
                    "email-account-warmup-modal__option",
                    isSelected && "is-selected",
                  )}
                >
                  <input
                    type="radio"
                    name="inbox-protection-plan"
                    className="email-account-warmup-modal__option-input"
                    checked={isSelected}
                    onChange={() => setSelectedPlan(option.value)}
                  />
                  <div className="email-account-warmup-modal__option-main">
                    <div className="email-account-warmup-modal__option-left">
                      <div className="email-account-warmup-modal__option-title-row">
                        <span className="email-account-warmup-modal__option-title">
                          {i18n.t(option.titleKey)}
                        </span>
                        {option.recommendedKey ? (
                          <span className="email-account-warmup-modal__option-badge">
                            {i18n.t(option.recommendedKey)}
                          </span>
                        ) : null}
                      </div>
                      <p className="email-account-warmup-modal__option-description">
                        {i18n.t(option.descriptionKey)}
                      </p>
                    </div>
                    <div className="email-account-warmup-modal__option-right">
                      <div className="email-account-warmup-modal__option-price-row">
                        {option.oldPriceKey ? (
                          <span className="email-account-warmup-modal__option-old-price">
                            {i18n.t(option.oldPriceKey)}
                          </span>
                        ) : null}
                        <span className="email-account-warmup-modal__option-price">
                        {i18n.t(option.priceKey)}
                        </span>
                      </div>
                      <span className="email-account-warmup-modal__option-total">
                      {i18n.t(option.totalKey)}
                      </span>
                    </div>
                  </div>
                </label>
              )
            })}
          </div>
        </div>

        <div className="email-account-warmup-modal__section">
          <h3 className="email-account-warmup-modal__section-title">
            {i18n.t(
              "js.email_accounts_center.email_account.warmup.protection_modal.payment_method",
            )}
          </h3>
          <div className="email-account-warmup-modal__payment-method">
            {!isEditingCard && currentCreditCard ? (
              <>
                <div className="email-account-warmup-modal__payment-card">
                  <CardLogo cardType={currentCreditCard.card_type} />
                  <span className="email-account-warmup-modal__payment-label">
                    {currentCreditCard.card_type} **** {currentCreditCard.last_4}
                  </span>
                  {formattedCardExpiration && (
                    <span className="email-account-warmup-modal__payment-expires">
                      · {i18n.t("js.expires")} {formattedCardExpiration}
                    </span>
                  )}
                </div>
                <Button
                  variant="link"
                  size="sm"
                  type="button"
                  className="email-account-warmup-modal__payment-edit"
                  onClick={handleEditPayment}
                >
                  {i18n.t(
                    "js.email_accounts_center.email_account.warmup.protection_modal.payment_method_edit",
                  )}
                </Button>
              </>
            ) : null}
          </div>

          {isCardFormVisible ? (
            <>
              <CardFormDisplay
                cardholderName={cardholderName}
                setCardholderName={setCardholderName}
                onCancel={handleCancelEditCard}
                showBackButton={!!currentCreditCard}
                showCardElement={showCardElement}
              />
              <div className="email-account-warmup-modal__card-actions">
                <Button
                  variant="primary"
                  size="sm"
                  type="button"
                  onClick={() => handleSaveCard(stripe, elements)}
                  disabled={isSavingCard}
                >
                  {isSavingCard
                    ? i18n.t("js.email_accounts_center.add_slots.saving_card")
                    : i18n.t("js.save_changes")}
                </Button>
              </div>
            </>
          ) : null}
        </div>

        <div className="email-account-warmup-modal__divider" />

        {isMulti ? (
          <div className="email-account-warmup-modal__subtotal">
            <span className="email-account-warmup-modal__subtotal-label">
              {i18n.t(
                "js.email_accounts_center.email_account.warmup.protection_modal.subtotal_label",
              )}
            </span>
            <span className="email-account-warmup-modal__subtotal-value">
              {i18n.t(
                "js.email_accounts_center.email_account.warmup.protection_modal.subtotal_value",
                {
                  count: selectedCount,
                  amount: formatUsdShort(perAccountTotal),
                },
              )}
            </span>
          </div>
        ) : null}

        <div className="email-account-warmup-modal__total">
          <span className="email-account-warmup-modal__total-label">
            {i18n.t(
              "js.email_accounts_center.email_account.warmup.protection_modal.total_due_today",
            )}
          </span>
          <span className="email-account-warmup-modal__total-value">
            {formatUsdTotal(totalDueToday)}
          </span>
        </div>
      </div>

      <DialogFooter className="email-account-warmup-modal__footer">
        <Button
          variant="primary"
          size="sm"
          type="button"
          fullWidth
          onClick={() => setIsSuccess(true)}
          disabled={!hasSelection}
        >
          {i18n.t(
            isMulti
              ? "js.email_accounts_center.email_account.warmup.protection_modal.cta_multi"
              : "js.email_accounts_center.email_account.warmup.protection_modal.cta",
            { count: selectedCount },
          )}
        </Button>
        <p
          className="email-account-warmup-modal__terms"
          dangerouslySetInnerHTML={{
            __html: i18n.t(
              "js.email_accounts_center.email_account.warmup.protection_modal.terms_html",
              { terms_url: terms_of_service_path() },
            ),
          }}
        />
      </DialogFooter>
    </>
  )

  const ModalBodyWithStripe = () => {
    const stripe = useStripe()
    const elements = useElements()
    return renderMainContent({
      stripe,
      elements,
      showCardElement: !!stripe && !!elements,
    })
  }

  const ModalBodyWithoutStripe = () =>
    renderMainContent({
      stripe: null,
      elements: null,
      showCardElement: false,
    })

  if (!isOpen) {
    return null
  }

  if (isSuccess) {
    return (
      <Dialog
        ref={dialogRef}
        id="inbox-protection-modal"
        className="email-account-warmup-modal"
        onClick={handleBackdropClick}
        onPointerDown={stopPropagation}
        onMouseDown={stopPropagation}
      >
        <DialogContent className="email-account-warmup-modal__content">
          <DialogBody className="email-account-warmup-modal__body">
            <Confetti />
            <div className="email-account-warmup-modal__success">
              <div className="email-account-warmup-modal__success-icon">
                <Icon icon="check" size="md" />
              </div>
              <h2 className="email-account-warmup-modal__success-title">
                {i18n.t(
                  "js.email_accounts_center.email_account.warmup.protection_modal.success.title",
                )}
              </h2>
              <div className="email-account-warmup-modal__success-message">
                <p>{i18n.t(primarySuccessMessageKey)}</p>
                <p>
                  {i18n.t(
                    "js.email_accounts_center.email_account.warmup.protection_modal.success.message_secondary",
                  )}
                </p>
              </div>
              <p className="email-account-warmup-modal__success-footnote">
                {i18n.t(
                  "js.email_accounts_center.email_account.warmup.protection_modal.success.footnote",
                  { date: nextInvoiceDate },
                )}
              </p>
            </div>
            <DialogFooter className="email-account-warmup-modal__footer">
              <Button
                variant="primary"
                size="sm"
                type="button"
                fullWidth
                onClick={close}
              >
                {i18n.t(
                  "js.email_accounts_center.email_account.warmup.protection_modal.success.cta",
                )}
              </Button>
            </DialogFooter>
          </DialogBody>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog
      ref={dialogRef}
      id="inbox-protection-modal"
      className="email-account-warmup-modal"
      onClick={handleBackdropClick}
      onPointerDown={stopPropagation}
      onMouseDown={stopPropagation}
    >
      <DialogContent className="email-account-warmup-modal__content">
        <DialogBody className="email-account-warmup-modal__body">
          {stripePromise ? (
            <Elements stripe={stripePromise}>
              <ModalBodyWithStripe />
            </Elements>
          ) : (
            <ModalBodyWithoutStripe />
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}
